import {
  Money,
  ChartBar,
  Buildings,
  Car,
  Coins,
  CreditCard,
  Diamond,
  Heart,
  House,
  Bank,
  Minus,
  Airplane,
  Plus,
  Repeat,
  Shield,
  ShoppingBag,
  ShoppingCart,
  TrendUp,
  ForkKnife,
  Lightning,
  PiggyBank,
  Play,
  Drop,
  Radio,
} from '@phosphor-icons/react';

import { bucketForRecurrence } from './incomeSourceBuckets';

/** Presets shown in onboarding step 4 (mockup). */
export const ONBOARDING_METHOD_PRESET_IDS = [
  'fifty_thirty_twenty',
  'jars',
  'eighty_twenty',
];

export const ONBOARDING_STEP_LABELS = [
  'Bem-vindo',
  'O teu nome',
  'Dia de fecho',
  'Método',
  'Fontes',
  'Categorias',
  'Ativos',
  'Dívidas',
  'Pronto',
];

/** Phase spans for WizardPhasedProgress (1-indexed inclusive). */
export const ONBOARDING_PHASES = [
  { id: 'about', name: 'Sobre ti', start: 1, end: 4 },
  { id: 'life', name: 'A tua vida financeira', start: 5, end: 8 },
  { id: 'done', name: 'Pronto', start: 9, end: 9 },
];

/** Sensible chip defaults (recognition, not creation). */
export const ONBOARDING_INCOME_PRESELECT = ['salary'];

export const ONBOARDING_CATEGORY_PRESELECT = [
  'Casa / Renda',
  'Supermercado',
  'Transportes',
  'Poupança',
];

export const ONBOARDING_ASSET_PRESELECT = ['bank_account'];

/** Collapsed card blurbs — meaning-first. */
export const METHOD_COLLAPSED_COPY = {
  fifty_thirty_twenty: 'Metade essencial, um terço para gostos, o resto poupança.',
  jars: 'Divide em 6 jarras com fins diferentes. Para quem gosta de detalhe.',
  eighty_twenty: 'Poupa 20%, vive com 80% sem contas ao detalhe.',
};

/**
 * Expanded meaning lines when a method card is selected.
 * `tone`: need | want | save | muted for colour chips.
 */
export const METHOD_MEANING_LINES = {
  fifty_thirty_twenty: [
    { tone: 'need', bold: 'Metade', text: ' para o essencial: casa, comida, contas' },
    { tone: 'want', bold: 'Um terço', text: ' para o que gostas: sair, lazer, extras' },
    { tone: 'save', bold: 'O resto', text: ' para poupar: o teu futuro' },
  ],
  jars: [
    { tone: 'need', bold: '6 jarras', text: ' com fins próprios: essencial, lazer, poupança…' },
    { tone: 'save', bold: null, text: 'Mais detalhe, para quem gosta de organizar ao pormenor' },
  ],
  eighty_twenty: [
    { tone: 'save', bold: 'Poupa primeiro', text: ' uma fatia e não penses mais nisso' },
    { tone: 'need', bold: 'Vive o resto', text: ' sem contas ao detalhe. O mais simples.' },
  ],
};

/** Maps app reliability buckets → onboarding SelectionGroup ids. */
const BUCKET_TO_GROUP = {
  guaranteed: 'fixed',
  variable: 'variable',
  occasional: 'occasional',
};

/** Group tint for selection chips: need | want | save (aligned with app 3 buckets). */
export const INCOME_GROUPS = [
  {
    id: 'fixed',
    name: 'Fixo',
    pctLabel: 'entra sempre',
    description: 'Rendimento com que podes contar todos os meses.',
    tint: 'need',
    dotClass: 'bg-sage-500',
    recurrenceKeys: ['MONTHLY_FIXED'],
  },
  {
    id: 'variable',
    name: 'Variável',
    pctLabel: 'muda todos os meses',
    description: 'Entra, mas nunca o mesmo, bom saber para não contar a mais.',
    tint: 'want',
    dotClass: 'bg-group-wants',
    recurrenceKeys: ['MONTHLY_VARIABLE'],
  },
  {
    id: 'occasional',
    name: 'Pontual',
    pctLabel: 'de vez em quando',
    description: 'Extras, capital, prendas, não contas com isto todos os meses.',
    tint: 'save',
    dotClass: 'bg-info-500',
    recurrenceKeys: ['OCCASIONAL'],
  },
];

export const INCOME_MOCKUP_ORDER = {
  fixed: ['salary', 'meal_card', 'rental', 'pension', 'subsidies'],
  variable: ['freelance', 'secondary'],
  occasional: [
    'capital_gains',
    'crypto',
    'extraordinary',
    'transfers',
    'family_help',
    'other_income',
  ],
};

export const INCOME_EXTRA_SUGGESTIONS = [
  {
    key: 'meal_card',
    label: 'Cartão refeição',
    category: 'salary',
    recurrence: 'monthly_fixed',
    icon: 'CreditCard',
  },
  {
    key: 'pension',
    label: 'Pensão',
    category: 'transfers',
    recurrence: 'monthly_fixed',
    icon: 'User',
  },
  {
    key: 'subsidies',
    label: 'Subsídios',
    category: 'transfers',
    recurrence: 'monthly_fixed',
    icon: 'Gift',
  },
  {
    key: 'family_help',
    label: 'Ajuda familiar',
    category: 'transfers',
    recurrence: 'occasional',
    icon: 'HeartHandshake',
  },
  {
    key: 'other_income',
    label: 'Outro',
    category: 'other',
    recurrence: 'occasional',
    icon: 'Plus',
  },
];

/** Resolve onboarding group id from suggestion recurrence (API or extras). */
export function incomeGroupIdForRecurrence(recurrence) {
  return BUCKET_TO_GROUP[bucketForRecurrence(recurrence).id] || 'fixed';
}

export function groupIncomeSuggestions(apiSuggestions) {
  const byKey = new Map();
  for (const item of apiSuggestions || []) {
    byKey.set(item.key, item);
  }
  for (const extra of INCOME_EXTRA_SUGGESTIONS) {
    if (!byKey.has(extra.key)) {
      byKey.set(extra.key, extra);
    }
  }

  const buckets = { fixed: [], variable: [], occasional: [] };
  const placed = new Set();

  for (const group of INCOME_GROUPS) {
    const order = INCOME_MOCKUP_ORDER[group.id] || [];
    for (const key of order) {
      const item = byKey.get(key);
      if (!item || placed.has(key)) continue;
      buckets[group.id].push({ ...item, tint: group.tint });
      placed.add(key);
    }
  }

  for (const item of byKey.values()) {
    if (placed.has(item.key)) continue;
    const groupId = incomeGroupIdForRecurrence(item.recurrence);
    const group = INCOME_GROUPS.find((g) => g.id === groupId) || INCOME_GROUPS[0];
    buckets[group.id].push({ ...item, tint: group.tint });
    placed.add(item.key);
  }

  return buckets;
}

export const CATEGORY_GROUPS = [
  {
    id: 'needs',
    name: 'Necessidades',
    pctLabel: 'o essencial · ~50%',
    description: 'Aquilo que não podes deixar de pagar: teto, comida, deslocações.',
    tint: 'need',
    dotClass: 'bg-sage-500',
    items: [
      { name: 'Casa / Renda', icon: House },
      { name: 'Supermercado', icon: ShoppingCart },
      { name: 'Transportes', icon: Car },
      { name: 'Contas casa', icon: Lightning },
      { name: 'Telecom.', icon: Radio },
      { name: 'Saúde', icon: Heart },
    ],
  },
  {
    id: 'wants',
    name: 'Desejos',
    pctLabel: 'o que gostas · ~30%',
    description: 'Extras que tornam a vida melhor mas podias cortar num mês apertado.',
    tint: 'want',
    dotClass: 'bg-group-wants',
    items: [
      { name: 'Restaurantes', icon: ForkKnife },
      { name: 'Lazer', icon: Play },
      { name: 'Compras', icon: ShoppingBag },
      { name: 'Subscrições', icon: Repeat },
      { name: 'Viagens', icon: Airplane },
    ],
  },
  {
    id: 'savings',
    name: 'Poupança',
    pctLabel: 'o teu futuro · ~20%',
    description: 'O que separas primeiro, antes de gastar o resto.',
    tint: 'save',
    dotClass: 'bg-group-savings',
    items: [
      { name: 'Poupança', icon: PiggyBank },
      { name: 'Investir', icon: ChartBar },
    ],
  },
];

export const ASSET_GROUPS = [
  {
    id: 'liquid',
    name: 'Disponível já',
    pctLabel: 'usas quando quiseres',
    description: 'Dinheiro à mão: contas, numerário, depósitos.',
    tint: 'need',
    dotClass: 'bg-sage-500',
    items: [
      { key: 'bank_account', label: 'Conta bancária', category: 'bank_account', icon: Bank },
      { key: 'cash', label: 'Dinheiro', category: 'cash', icon: Money },
      { key: 'savings', label: 'Depósito', category: 'savings_account', icon: Drop },
      { key: 'meal_card_asset', label: 'Cartão refeição', category: 'bank_account', icon: CreditCard },
    ],
  },
  {
    id: 'invested',
    name: 'Investido',
    pctLabel: 'tempo ou custo p/ usar',
    description: 'Dinheiro a trabalhar: leva tempo ou tem custo a mobilizar.',
    tint: 'save',
    dotClass: 'bg-group-savings',
    items: [
      { key: 'brokerage', label: 'Ações / ETFs', category: 'brokerage', icon: TrendUp },
      { key: 'crypto', label: 'Cripto', category: 'crypto', icon: Coins },
      { key: 'gold', label: 'Ouro', category: 'precious_metal', icon: Diamond },
      { key: 'real_estate', label: 'Imóveis', category: 'real_estate', icon: Buildings },
      { key: 'savings_cert', label: 'Cert. aforro', category: 'savings_account', icon: Shield },
      { key: 'other', label: 'Outro', category: 'other', icon: Plus },
    ],
  },
];

export const ONBOARDING_DEFINITIONS = {
  income:
    'Uma fonte de rendimento é de onde te entra dinheiro. Separamos as fixas (entram sempre) das variáveis (mudam todos os meses).',
  categories:
    'Uma categoria agrupa despesas parecidas. Cada uma pertence a um dos 3 grupos do método que escolheste.',
  assets:
    'Um ativo é um sítio onde tens dinheiro. Separamos o disponível já do que está investido.',
  debts:
    'Uma dívida é dinheiro que deves e vais pagando. Saber quais tens ajuda a app a priorizar.',
};

export const DEBT_GROUPS = [
  {
    id: 'credits',
    name: 'Créditos',
    pctLabel: 'opcional',
    description: 'Escolhe as que tens. Sem culpa nenhuma se saltares este passo.',
    tint: 'want',
    dotClass: 'bg-group-wants',
    items: [
      { key: 'credit_card', label: 'Cartão de crédito', icon: CreditCard },
      { key: 'personal_loan', label: 'Crédito pessoal', icon: Money },
      { key: 'mortgage', label: 'Crédito habitação', icon: House },
      { key: 'auto_loan', label: 'Crédito automóvel', icon: Car },
      { key: 'none', label: 'Nenhuma', icon: Minus },
    ],
  },
];
